/**
 * Bound an operation in time, and cancel what it was doing when the time runs out.
 *
 * Two flows used to have no deadline at all: the consent provider (so one request that was accepted
 * and never answered wedged `machine.js`'s serialised write chain for the life of the tab) and the
 * join (library worst case: about 25s of retry pauses plus six request timeouts, all spent with a
 * spinner in front of the fan). This is the one helper both use.
 *
 * **It aborts, it does not just stop waiting.** `run` is handed an `AbortSignal` and is expected to
 * pass it to whatever it sends, so a timed-out request is cancelled rather than left to land later
 * unobserved. ./leaveLoyalty.js's older `withDeadline` only stops waiting, and says so.
 *
 * **There is no "no deadline" branch.** A missing or non-positive `ms` takes FALLBACK_MS rather than
 * running unbounded -- an unbounded wait is the defect this file exists to remove.
 *
 * Timers are injected so deadline.test.js can drive expiry without waiting on a clock.
 */

/** Used when a caller passes no usable deadline. */
export const FALLBACK_MS = 30000;

/** What a run that ran out of time rejects with, unless the caller supplies its own error. */
export class DeadlineError extends Error {
	/**
	 * @param {number} ms The deadline that was exceeded.
	 */
	constructor( ms ) {
		super( `The operation did not finish within ${ ms }ms.` );
		this.name = 'DeadlineError';
		this.ms = ms;
		this.timedOut = true;
	}
}

const defaultTimers = {
	set: ( fn, ms ) => setTimeout( fn, ms ),
	clear: handle => clearTimeout( handle ),
};

/**
 * @return {{signal: (AbortSignal|undefined), abort: Function}} A controller, or an inert stand-in
 *                                                              where the engine has none.
 */
function createController() {
	if ( typeof AbortController === 'function' ) {
		return new AbortController();
	}

	return { signal: undefined, abort: () => {} };
}

/**
 * @param {(signal: (AbortSignal|undefined)) => *} run              The operation. May return a promise.
 * @param {number}                                 ms               How long to allow.
 * @param {Object}                                 [opts]           Options.
 * @param {{set: Function, clear: Function}}       [opts.timers]    Timer source.
 * @param {(ms: number) => Error}                  [opts.makeError] The rejection on expiry.
 * @param {AbortSignal}                            [opts.signal]    An outer signal; aborting it
 *                                                                  aborts the run as well.
 * @return {Promise<*>} What `run` resolved with, or a rejection once the time is up.
 */
export function withDeadline( run, ms, { timers = defaultTimers, makeError, signal } = {} ) {
	const limit = Number( ms ) > 0 ? Number( ms ) : FALLBACK_MS;
	const controller = createController();
	let timer = null;

	if ( signal && typeof signal.addEventListener === 'function' ) {
		if ( signal.aborted ) {
			controller.abort();
		} else {
			signal.addEventListener( 'abort', () => controller.abort(), { once: true } );
		}
	}

	const expiry = new Promise( ( resolve, reject ) => {
		timer = timers.set( () => {
			controller.abort();
			reject( makeError ? makeError( limit ) : new DeadlineError( limit ) );
		}, limit );
	} );

	return Promise.race( [
		Promise.resolve().then( () => run( controller.signal ) ),
		expiry,
	] ).finally( () => {
		timers.clear( timer );
	} );
}

/**
 * Whether an error is a deadline running out -- this module's, or one a caller built with
 * `timedOut: true` through `makeError`.
 *
 * @param {*} error Anything.
 * @return {boolean} True for a timeout.
 */
export function isTimeout( error ) {
	return Boolean( error && error.timedOut === true );
}
